import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { transform_languages, transform_timezone, CREDIT_COST, CREDIT_PER_MINUTE } from "../../components/Transformation/Transformations";


export default function ConfirmationCard(props) {
  const [studentMeetingDateTime, setStudentMeetingDateTime] = useState(moment());
  const [neededCredits, setNeededCredits] = useState(0);
  const userProfile = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (props?.meeting_happen_at) {
      // Parse the date string using the user's timezone
      const localDateTime = moment(props?.meeting_happen_at);

      // Format the date and time
      const formattedDate = localDateTime.format("YYYY-MMM-DD");
      const formattedTime = localDateTime.format("HH:mm");
      setStudentMeetingDateTime(moment.tz(formattedDate + " " +formattedTime, '"YYYY-MMM-DD" HH:mm', userProfile?.timezone));
    }
  }, [props.meeting_happen_at, userProfile]);

  useEffect(() => {
    // Calculate needed credits based on the meeting duration and leader_profile's credit_adjustment_rate
    const calculateNeededCredits = () => {
      const durationMinutes = parseInt(props.duration, 10) || 0;
      const creditPerMinute = CREDIT_PER_MINUTE || 1;
      const rate =
        props.credit_adjustment_rate !== undefined &&
        props.credit_adjustment_rate !== null &&
        !isNaN(Number(props.credit_adjustment_rate))
          ? Number(props.credit_adjustment_rate)
          : 60;
      const creditsRequired = Math.ceil(((durationMinutes / creditPerMinute) * rate) / 60);
      setNeededCredits(isNaN(creditsRequired) ? 0 : creditsRequired);
    };

    calculateNeededCredits();
  }, [props.duration, props.credit_adjustment_rate]);

  const userCredits = Number(props.credits) || 0;
  const hasEnoughCredits = userCredits >= neededCredits;

  const handleNavigate = () => {
    navigate("/user/payment");
  };

  let teacher_meeting_happen_at = props.leaderTimezone
    ? studentMeetingDateTime.clone().tz(props.leaderTimezone)
    : studentMeetingDateTime;

  return (
    <div className="flex flex-col rounded-[var(--radius)] shadow-lg bg-white w-[320px] h-auto md:w-[520px] md:h-auto p-6">
      <div className="flex gap-5 md:gap-10 border-b-2 pb-4">
        <div className="flex-shrink-0">
          <img
            src={props.image}
            alt="userImage"
            className="rounded-full border-solid border-2 border-gray-300 w-[80px] h-[80px] md:w-[110px] md:h-[110px]"
          />
        </div>
        <div className="flex flex-col justify-center">
          <div className="text-2xl font-bold text-black">{props.name}</div>
          <div className="text-sm font-medium text-gray-800 mt-0">
            {t(props.job)}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 mt-4">
        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">{t("confirmation_card.datetime_teacher")}</div>
          <div className="text-lg font-normal text-gray-800">
            {teacher_meeting_happen_at.format('YYYY-MMM-DD HH:mm')} {t(transform_timezone(props.leaderTimezone))}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">{t("confirmation_card.datetime_student")}</div>
          <div className="text-lg font-normal text-gray-800">
            {studentMeetingDateTime.format('YYYY-MMM-DD HH:mm')} {t(transform_timezone(userProfile?.timezone))}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">{t("confirmation_card.du")}</div>
          <div className="text-lg font-normal text-gray-800">
            {props.duration} {t("generic.minute")}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">{t("confirmation_card.lang")}</div>
          <div className="text-lg font-normal text-gray-800">{t(transform_languages(props.teaching_language))}</div>
        </div>
        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">{t("confirmation_card.your_credit")}</div>
          <div className="text-lg font-normal text-gray-800">{props.credits ?? 0}</div>
        </div>


        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">
            {t("confirmation_card.need_credits")}
          </div>
          <div className="text-lg font-normal text-gray-800">
            {neededCredits}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="text-xl font-semibold text-gray-900">
            {t("confirmation_card.need_dollar")}
          </div>
          <div className="text-lg font-normal text-gray-800">
            ${((neededCredits * (CREDIT_COST || 0.1666667))).toFixed(2)}
          </div>
        </div>
        {neededCredits > userCredits && (
          <div
            className="text-sm font-medium text-red-400 cursor-pointer underline w-[55%]"
            onClick={() => {
              handleNavigate();
            }}
          >
            {t("confirmation_card.error_text")}
          </div>
        )}


      </div>
      <div className="flex flex-col mt-6">
        <label className="text-lg font-semibold text-gray-900 mb-2">
          {t("confirmation_card.notes_label") || "Notes (Optional)"}
        </label>
        <textarea
          className="w-full h-24 p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[var(--brand)] resize-none"
          placeholder={t("confirmation_card.notes_placeholder") || "Add any notes or special requests..."}
          value={props.userNotes}
          onChange={(e) => props.setUserNotes(e.target.value)}
        />
      </div>
      <div className="flex justify-center mt-6 gap-4">
        <button
          className="bg-[var(--brand)] hover:bg-[var(--brand-dark)] transition-colors w-[120px] h-[40px] md:w-[145px] md:h-[50px] text-xl xl:text-2xl font-medium text-white rounded-full"
          onClick={props.meetingCancel}
        >
          {t("confirmation_card.cancel_button")}
        </button>
        <button
          className={`w-[120px] h-[40px] md:w-[145px] md:h-[50px] text-xl xl:text-2xl font-medium text-white rounded-full transition-colors ${
            hasEnoughCredits
              ? "bg-[var(--brand)] hover:bg-[var(--brand-dark)] cursor-pointer"
              : "bg-gray-500 cursor-not-allowed"
          }`}
          onClick={props.meetingSubmit}
          disabled={!hasEnoughCredits}
        >
          {t("confirmation_card.submit_button")}
        </button>
      </div>
    </div>
  );
}
